import { useAdminExtensionSyncs, type ExtensionSync } from '@fidalia/api';
import {
  Alert,
  Avatar,
  Badge,
  DataTable,
  Input,
  PageHeader,
  Pagination,
  Select,
  type TableColumn,
} from '@fidalia/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { MdCheck, MdClose, MdSearch, MdSync } from 'react-icons/md';

import { errorMessage } from '@/lib/auth';
import { Quand } from '@/lib/quand';
import { useDebounced } from '@/lib/use-debounced';

const PAGE_SIZE = 50;

export const Route = createFileRoute('/_app/syncs')({
  component: SynchronisationsPage,
});

const TYPES = {
  doctolib: 'Relevé Doctolib',
  appointments: 'Rendez-vous',
} as const;

const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`;

/** Ce que la synchronisation a apporté, en une ligne. */
function resume(sync: ExtensionSync): string {
  const s = sync.stats;
  if (sync.kind === 'doctolib') {
    return `${pluriel(s.agendas ?? 0, 'agenda')} · ${pluriel(s.motifs ?? 0, 'motif')}`;
  }
  const parts = [`${s.received ?? 0} reçus`];
  if (s.created) parts.push(`${s.created} créés`);
  if (s.updated) parts.push(`${s.updated} modifiés`);
  if (s.cancelled) parts.push(`${s.cancelled} annulés`);
  return parts.join(' · ');
}

const columns: TableColumn<ExtensionSync>[] = [
  {
    key: 'quand',
    title: 'Quand',
    cell: (s) => <Quand date={s.createdAt} />,
  },
  {
    key: 'cabinet',
    title: 'Cabinet',
    cell: (s) => (
      <div className="flex items-center gap-3">
        <Avatar name={s.cabinetNom} size="sm" tone="auto" />
        {s.cabinetUserId ? (
          <Link
            to="/users/$id"
            params={{ id: s.cabinetUserId }}
            className="font-medium text-brand-600 hover:underline"
          >
            {s.cabinetNom}
          </Link>
        ) : (
          <span className="font-medium">{s.cabinetNom}</span>
        )}
      </div>
    ),
  },
  {
    key: 'membre',
    title: 'Membre',
    cell: (s) =>
      s.member ? (
        <div className="flex min-w-0 flex-col">
          <span className="font-medium">{s.member.nom}</span>
          <span className="truncate text-xs text-content-muted">{s.member.email}</span>
        </div>
      ) : (
        <span className="text-content-subtle">—</span>
      ),
  },
  {
    key: 'type',
    title: 'Type',
    cell: (s) => (
      <Badge tone="neutral" size="sm">
        {TYPES[s.kind]}
      </Badge>
    ),
  },
  {
    key: 'resume',
    title: 'Résultat',
    cell: (s) =>
      s.ok ? (
        <span className="text-sm text-content-muted">{resume(s)}</span>
      ) : (
        <span className="line-clamp-2 max-w-xs text-sm text-danger" title={s.error ?? undefined}>
          {s.error ?? 'Échec'}
        </span>
      ),
  },
  {
    key: 'statut',
    title: 'Statut',
    cell: (s) =>
      s.ok ? (
        <Badge tone="success" icon={<MdCheck className="size-3.5" />}>
          Réussie
        </Badge>
      ) : (
        <Badge tone="danger" icon={<MdClose className="size-3.5" />}>
          Échec
        </Badge>
      ),
  },
];

function SynchronisationsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [kind, setKind] = useState<'doctolib' | 'appointments' | ''>('');
  const [status, setStatus] = useState<'ok' | 'error' | ''>('');

  /** Tout changement de filtre repart de la première page. */
  const filtrer =
    <T,>(setter: (valeur: T) => void) =>
    (valeur: T) => {
      setter(valeur);
      setPage(1);
    };

  const { data, error } = useAdminExtensionSyncs({
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch,
    kind,
    status,
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdSync />}
        title="Synchronisations"
        description="Ce que l’extension envoie : relevés de la configuration Doctolib et rendez-vous, avec ce qu’ils ont donné. Le journal commence à la mise en service de cette page."
      />

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          type="search"
          aria-label="Rechercher une synchronisation"
          placeholder="Cabinet, membre, e-mail…"
          icon={<MdSearch className="size-6" />}
          containerClassName="h-11 w-full sm:min-w-64 sm:flex-1"
          value={search}
          onChange={(event) => filtrer(setSearch)(event.target.value)}
        />

        <Select
          aria-label="Filtrer par type"
          containerClassName="h-11 w-full sm:w-52"
          value={kind}
          onChange={(event) => filtrer(setKind)(event.target.value as typeof kind)}
        >
          <option value="">Tous les types</option>
          <option value="doctolib">Relevé Doctolib</option>
          <option value="appointments">Rendez-vous</option>
        </Select>

        <Select
          aria-label="Filtrer par statut"
          containerClassName="h-11 w-full sm:w-48"
          value={status}
          onChange={(event) => filtrer(setStatus)(event.target.value as typeof status)}
        >
          <option value="">Tous les statuts</option>
          <option value="ok">Réussies</option>
          <option value="error">En échec</option>
        </Select>

        {data ? (
          <p className="text-sm text-content-muted sm:ml-auto">
            <span className="font-semibold text-content">{data.total}</span> synchronisation
            {data.total > 1 ? 's' : ''}
          </p>
        ) : null}
      </div>

      <DataTable
        mobileCards
        data={data?.items ?? []}
        columns={columns}
        isLoading={!data}
        skeletonRows={10}
        rowId={(s) => s.id}
        emptyMessage={
          debouncedSearch
            ? `Aucun résultat pour « ${debouncedSearch} ».`
            : 'Aucune synchronisation reçue pour l’instant.'
        }
      />

      {data ? (
        <Pagination
          page={page}
          total={data.total}
          limit={PAGE_SIZE}
          onChange={setPage}
          label="synchronisations"
        />
      ) : null}
    </div>
  );
}
